import React, { useState, useEffect, useCallback, useMemo, useRef, startTransition } from 'react';

import { useStore } from 'zustand';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAppStore } from '../../../store/useAppStore';
import { logout } from '../../auth';
import { Permission } from '../../auth/types';

import {
    getCloudProject,
    getUserProjects,
    splitCloudProjectSnapshotForHydration,
    useConflictReviewStore,
    useProjectWriteDispatcher,
    useSync,
} from '../../sync';
import {
    includeScriptBlockAuthority,
    ProjectDataAuthorityProvider,
} from '../../sync/project-data-readiness';
import {
    deriveNeededProjectDataResources,
    deriveProjectDataResources,
    getFirebaseProjectListenersForRequirements,
    type ProjectDataRequirementInput,
} from '../../sync/project-data-requirements';
import { useProjectJoin } from '../../sync/hooks/useProjectJoin';
import { useProjectListenersSync } from '../../sync/hooks/useProjectListenersSync';
import { useRawNotesSync } from '../../sync/hooks/useRawNotesSync';
import type { ProjectScope } from '../../sync/project-scope';
import { getProjectWriteHeaderStatus } from '../../sync/project-write-status';
import { ConflictReviewPanel } from '../../sync/components/ConflictReviewPanel';
import { isBarryAvailableForProject } from '../../barry/beta-gate';
import { useOrganizationContext } from '../../admin/hooks/useOrganizationContext';
import { useProjectOrganizationBranding } from '../../admin/hooks/useProjectOrganizationBranding';
import { useLayoutAuthSession } from '../hooks/useLayoutAuthSession';
import { confirmDialog, showToast } from '@/utils/notifications';

import { useKeyboardNavigation } from '@/hooks/useKeyboardNavigation';
import { usePWA } from '@/contexts/pwa-hooks';
import { cn } from '@/utils/cn';
import { motion } from 'motion/react';
import { useMotionConfig } from '@/hooks/useMotionConfig';
import { logBetaActiveProjectDay } from '@/utils/beta-analytics';
import { openSentryFeedbackDialog } from '@/sentry';

// Components
import { Header } from './Header';
import { MobileShellLauncher } from './MobileShellLauncher';
import { WorkspaceSidebar } from './WorkspaceSidebar';
import { WorkspaceSelector } from './WorkspaceSelector';

// Static imports — lightweight or always-visible components
import { ImportScriptButton } from '../../files/components/ImportButtons';
import { ScriptJobStatusChip } from '../../show-structure/components/ScriptJobStatusChip';
import { useScriptJobStatus } from '../../show-structure/hooks/useScriptJobStatus';
import { useScriptBlocks } from '../../show-structure/hooks/useScriptBlocks';
import { Button, Icon, ErrorBoundary, ToolActionBar, UndoRedoButtons, Spinner, DeviceGateBanner, Input, FileUploadButton } from '../../../components/common';
import { DashboardPage } from '../../dashboard/pages/DashboardPage';
import { NotificationTray } from '@/features/notifications/components/NotificationTray';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';
import { useDeviceGate } from '@/hooks/useDeviceGate';
import { usePersistentScrollRestoration } from '@/hooks/usePersistentUxState';
import { AnimatePresence } from 'motion/react';

// Lazy-loaded Tab Components — heavy feature modules loaded on demand
const FeedTab = React.lazy(() => import('../../feed/components/FeedTab').then(m => ({ default: m.FeedTab })));
const SetBuilderTab = React.lazy(() => import('../../sets/components/SetBuilderTab').then(m => ({ default: m.SetBuilderTab })));
const BlockingTab = React.lazy(() => import('../../blocking/components/BlockingTab').then(m => ({ default: m.BlockingTab })));
const NotesTab = React.lazy(() => import('../../notes/components/NotesTab').then(m => ({ default: m.NotesTab })));
const ActionItemsTab = React.lazy(() => import('../../action-items/components/ActionItemsTab').then(m => ({ default: m.ActionItemsTab })));
const LineNotesTab = React.lazy(() => import('../../notes/components/LineNotesTab').then(m => ({ default: m.LineNotesTab })));
const SoundTab = React.lazy(() => import('../../sound/components/SoundTab').then(m => ({ default: m.SoundTab })));
const ShowStructureTab = React.lazy(() => import('../../show-structure/components/ShowStructureTab').then(m => ({ default: m.ShowStructureTab })));
const RehearsalScriptTab = React.lazy(() => import('../../show-structure/components/RehearsalScriptTab').then(m => ({ default: m.RehearsalScriptTab })));
const PersonnelTab = React.lazy(() => import('../../personnel/components/PersonnelTab').then(m => ({ default: m.PersonnelTab })));
const PropsTab = React.lazy(() => import('../../props/components/PropsTab').then(m => ({ default: m.PropsTab })));
const CostumesTab = React.lazy(() => import('../../costumes/components/CostumesTab').then(m => ({ default: m.CostumesTab })));
const RunSheetTab = React.lazy(() => import('../../run-sheet/components/RunSheetTab').then(m => ({ default: m.RunSheetTab })));
const SchedulerTab = React.lazy(() => import('../../scheduler/components/SchedulerTab').then(m => ({ default: m.SchedulerTab })));
const CalendarView = React.lazy(() => import('../../calendar/components/CalendarView').then(m => ({ default: m.CalendarView })));
const SettingsTab = React.lazy(() => import('../../settings/components/SettingsTab').then(m => ({ default: m.SettingsTab })));
const FileExplorer = React.lazy(() => import('../../files/components/FileExplorer').then(m => ({ default: m.FileExplorer })));
const KitchenSink = React.lazy(() => import('../../../modules/dev/KitchenSink').then(m => ({ default: m.KitchenSink })));
const BudgetTab = React.lazy(() => import('../../budget').then(m => ({ default: m.BudgetTab })));
const ProductionTab = React.lazy(() => import('../../production').then(m => ({ default: m.ProductionTab })));
const VideoView = React.lazy(() => import('../../video/components/VideoView').then(m => ({ default: m.VideoView })));
const LightingTab = React.lazy(() => import('../../lighting/components/LightingTab').then(m => ({ default: m.LightingTab })));
const PromptBookTab = React.lazy(() => import('../../prompt-book/components/PromptBookTab').then(m => ({ default: m.PromptBookTab })));
const EmailTab = React.lazy(() => import('../../email/components/EmailTab').then(m => ({ default: m.EmailTab })));
const PerformanceTab = React.lazy(() => import('../../performance/components/PerformanceTab').then(m => ({ default: m.PerformanceTab })));
const ActivityLogTab = React.lazy(() => import('../../activity-log/components/ActivityLogTab').then(m => ({ default: m.ActivityLogTab })));
const BarryPanel = React.lazy(() => import('../../barry/BarryPanel').then(m => ({ default: m.BarryPanel })));
const CommandPaletteComponent = React.lazy(() => import('../../command-palette/CommandPalette').then(m => ({ default: m.CommandPalette })));

// Lazy-loaded Modals — only rendered when open
const AuthModal = React.lazy(() => import('../../auth/components/AuthModal').then(m => ({ default: m.AuthModal })));
const AccountModal = React.lazy(() => import('../../auth/components/AccountModal').then(m => ({ default: m.AccountModal })));
const ReportsModal = React.lazy(() => import('../../reports/components/ReportsModal').then(m => ({ default: m.ReportsModal })));
const ImportDeadlinesModal = React.lazy(() => import('../../production/components/ImportDeadlinesModal').then(m => ({ default: m.ImportDeadlinesModal })));
const ImportPropsModal = React.lazy(() => import('../../props/components/ImportPropsModal').then(m => ({ default: m.ImportPropsModal })));
import { ExtractPropsButton, ExtractSoundCuesButton } from './ExtractButtons';

import {
    WorkspaceId,
    ToolId,
    ToolConfig,
    TOOLS,
    getDefaultWorkspace,
    getAccessibleTools,
    getAccessibleWorkspaces,
    filterScriptGatedTools,
    getRenderableToolId,
    getRenderableOptionalToolId,
    hasResolvedScriptTextToolAccess,
    getWorkspace,
    getTool,
    getRouteForTool,
    getToolByRoute,
    isToolId,
} from '../workspace-config';
import { resolveWorkspacePrimaryToolIds } from '../workspace-tool-order';
import { PHONE_SHELL_CONTENT_OFFSET_CLASS, shouldHidePhoneShellChrome } from '../mobile-shell';
import { getRoleById, DEFAULT_ROLES } from '../../auth/permissions';
import { SplitLayout } from './SplitLayout';
import { useCommandPalette } from '../../command-palette/useCommandPalette';
import { TOOL_REPORTS, type ReportId } from '../../reports/report-registry';
import { canAccessProjectViaOrgAdmin, getLayoutUserPermissions } from '../layout-permissions';

type AuthModalMode = 'signin' | 'signup';

const INTERNAL_SCROLL_TOOL_IDS = new Set<ToolId>([
    'show-structure',
    'rehearsal-script',
    'line-notes',
    'blocking',
    'set-builder',
    'prompt-book',
    'scheduler',
    'performance',
]);

// fallow-ignore-next-line complexity
export const MainLayout: React.FC = () => {
    // Store State
    const projectMeta = useAppStore(state => state.projectMeta);
    const { updateProjectMeta, setActiveWorkspace, toggleSidebarCollapsed, clearCloudSession } = useAppStore(state => state.metaActions);
    const { undo: appUndo, redo: appRedo, futureStates, pastStates } = useStore(useAppStore.temporal);
    const activeWorkspace = useAppStore(state => state.ui.activeWorkspace);
    const sidebarCollapsed = useAppStore(state => state.ui.sidebarCollapsed);
    const activeTab = useAppStore(state => state.ui.activeTab || 'show-structure');
    const workspaceToolOrderOverrides = useAppStore(state => state.ui.workspaceToolOrderOverrides);
    const motionConfig = useMotionConfig();

    // Organization logo for the header
    const { organization, activeOrgId: adminActiveOrgId, memberships: orgMemberships = [] } = useOrganizationContext();
    const { logo: projectOrganizationLogo } = useProjectOrganizationBranding();

    // Script import job status (sidebar badge + toolbar chip)
    const scriptJobState = useScriptJobStatus();

    // Local UI State
    const { setActiveTab: setStoreActiveTab } = useAppStore(state => state.metaActions);
    const { clearAuthUser, hasResolvedAuth, user } = useLayoutAuthSession();
    const [searchTerm, setSearchTerm] = useState('');
    
    // Modal States
    const [isAuthModalOpen, setAuthModalOpen] = useState(false);
    const [authModalInitialMode, setAuthModalInitialMode] = useState<AuthModalMode>('signin');
    const [isAccountModalOpen, setAccountModalOpen] = useState(false);
    const [isReportsModalOpen, setReportsModalOpen] = useState(false);
    const [isWorkspaceSelectorOpen, setWorkspaceSelectorOpen] = useState(false);
    const [splitViewActive, setSplitViewActive] = useState(false);
    const [splitRightToolId, setSplitRightToolId] = useState<ToolId | null>(null);
    const [activeReportId, setActiveReportId] = useState<ReportId | null>(null);
    const [csvImportContent, setCsvImportContent] = useState<string | null>(null);
    const [csvImportSource, setCsvImportSource] = useState<ToolId | null>(null);
    const [splitFocusedSide, setSplitFocusedSide] = useState<'left' | 'right'>('left');

    const activeToolId = useMemo<ToolId>(
        () => isToolId(activeTab) ? activeTab : 'show-structure',
        [activeTab],
    );
    const { scrollRef: mainScrollRef, handleScroll: handleMainScroll } = usePersistentScrollRestoration(activeToolId);
    const projectHydrationToolIds = useMemo<ToolId[]>(() => {
        const toolIds: ToolId[] = [activeToolId];
        if (splitViewActive && splitRightToolId && splitRightToolId !== activeToolId) {
            toolIds.push(splitRightToolId);
        }
        return toolIds;
    }, [activeToolId, splitRightToolId, splitViewActive]);
    const projectHydrationReportIds = useMemo<ReportId[]>(
        () => isReportsModalOpen && activeReportId ? [activeReportId] : [],
        [activeReportId, isReportsModalOpen],
    );
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const cloudProjectId = useAppStore(state => state.cloud.projectId);
    const cloudOrgId = useAppStore(state => state.cloud.orgId);
    const canAccessProjectAsOrgAdmin = useMemo(
        () => canAccessProjectViaOrgAdmin(user, cloudOrgId, orgMemberships),
        [cloudOrgId, orgMemberships, user],
    );
    const projectDataRequirements = useMemo<ProjectDataRequirementInput>(() => ({
        toolIds: projectHydrationToolIds,
        reportIds: projectHydrationReportIds,
        surfaces: ['layoutSidebar'],
    }), [projectHydrationReportIds, projectHydrationToolIds]);
    const neededProjectDataResources = useMemo(
        () => deriveNeededProjectDataResources(projectDataRequirements),
        [projectDataRequirements],
    );
    const neededProjectResources = useMemo(
        () => deriveProjectDataResources(projectDataRequirements),
        [projectDataRequirements],
    );
    const projectListenerIds = useMemo(
        () => getFirebaseProjectListenersForRequirements(projectDataRequirements),
        [projectDataRequirements],
    );
    // Initialize project hydration from the manifest-derived data contract.
    const projectSyncAuthority = useSync({ projectDataResources: neededProjectDataResources });
    useRawNotesSync(neededProjectResources.includes('rawNotes'), {
        canAccessAsOrgAdmin: canAccessProjectAsOrgAdmin,
    });
    useProjectJoin();
    const scriptBlocksRequired = neededProjectResources.includes('scriptBlocks');
    const scriptBlocksReadiness = useScriptBlocks(scriptBlocksRequired);
    const projectDataAuthority = useMemo(() => includeScriptBlockAuthority({
        projectAuthority: projectSyncAuthority,
        scriptBlocksAuthoritative: scriptBlocksReadiness.hasLoaded,
        scriptBlocksRequired,
    }), [projectSyncAuthority, scriptBlocksReadiness.hasLoaded, scriptBlocksRequired]);

    // Mobile sidebar state
    const [isMobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // Notification system
    const [trayOpen, setTrayOpen] = useState(false);
    const [isConflictReviewOpen, setConflictReviewOpen] = useState(false);
    const { notifications, unreadCount, loading: notificationsLoading } = useNotifications();

    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const desktopSidebarRef = useRef<HTMLDivElement | null>(null);
    const routeProjectLoadRef = useRef<string | null>(null);
    const loadedRouteProjectRef = useRef<string | null>(null);
    const workspaceDefaultScopeRef = useRef<string | null>(null);

    const openAuthModal = useCallback((mode: AuthModalMode = 'signin') => {
        setAuthModalInitialMode(mode);
        setAuthModalOpen(true);
    }, []);

    // Get user's permissions
    const cloudMember = useAppStore(state => state.cloud.member);
    const roles = useAppStore(useCallback(state => state.admin.roles, []));
    const userRole = useMemo(() => {
        if (!user || !cloudMember) return null;
        // Try project-specific roles first, then fall back to built-in defaults
        const effectiveRoles = roles.length > 0 ? roles : DEFAULT_ROLES;
        return getRoleById(effectiveRoles, cloudMember.roleId) ?? null;
    }, [cloudMember, roles, user]);
    
    // Super admins get all permissions for workspace visibility
    // Memoize to prevent infinite loop in effects
    const userPermissions = useMemo(
        () => getLayoutUserPermissions(user, userRole, canAccessProjectAsOrgAdmin),
        [canAccessProjectAsOrgAdmin, user, userRole],
    );
    const canSyncCostumeCollections = userPermissions.includes(Permission.EDIT_COSTUMES);
    useProjectListenersSync({
        canAccessAsOrgAdmin: canAccessProjectAsOrgAdmin,
        canSyncCostumes: canSyncCostumeCollections,
        listenerIds: projectListenerIds,
    });

    // Get accessible tools (filtered by permissions + script text gating)
    const rawScriptTextAccess = useAppStore(state => state.cloud.scriptTextAccess);
    const scriptTextAccess = rawScriptTextAccess ?? false;
    const hasResolvedScriptTextAccess = hasResolvedScriptTextToolAccess({
        cloudEnabled,
        scriptTextAccess: rawScriptTextAccess,
    });
    const accessibleTools = useMemo(() => filterScriptGatedTools(getAccessibleTools(userPermissions), scriptTextAccess), [userPermissions, scriptTextAccess]);
    const accessibleWorkspaces = useMemo(() => getAccessibleWorkspaces(userPermissions), [userPermissions]);
    const accessibleToolIds = useMemo(() => new Set<ToolId>(accessibleTools.map(tool => tool.id)), [accessibleTools]);
    const accessibleWorkspaceIds = useMemo(() => new Set<WorkspaceId>(accessibleWorkspaces.map(workspace => workspace.id)), [accessibleWorkspaces]);
    const routeFallbackTool = useMemo(() => (
        accessibleTools.find(tool => tool.id === 'dashboard')
        ?? accessibleTools[0]
        ?? getTool('show-structure')
    ), [accessibleTools]);

    useEffect(() => {
        if (!hasResolvedAuth || user) return;
        if (!sessionStorage.getItem('pendingJoin')) return;

        const pendingMode = sessionStorage.getItem('pendingJoinAuthMode');
        openAuthModal(pendingMode === 'signup' ? 'signup' : 'signin');
    }, [hasResolvedAuth, user, openAuthModal]);

    // Centralized Project Guard: used by routing guards and project switch logic
    const hasLoadedCloudProject = useAppStore(state => state.cloud.hasLoadedCloudProject === true);
    const isBetaProject = useAppStore(state => state.cloud.isBeta === true);
    const showBarryPanel = isBarryAvailableForProject(isBetaProject);
    const routeProjectId = searchParams.get('projectId')?.trim() || '';
    const routeOrgId = (searchParams.get('orgId') || searchParams.get('org') || '').trim();
    const hasInviteRouteParams = Boolean(
        searchParams.get('token')
        || searchParams.get('invitation')
        || searchParams.get('invitationId')
    );
    const projectWriteScope = useMemo<ProjectScope | null>(() => (
        cloudOrgId && cloudProjectId ? { orgId: cloudOrgId, projectId: cloudProjectId } : null
    ), [cloudOrgId, cloudProjectId]);
    const adminOrganizationLogo = !cloudOrgId || adminActiveOrgId === cloudOrgId ? organization?.logo : undefined;
    const headerRightLogo = projectOrganizationLogo || adminOrganizationLogo || projectMeta.rightLogo || '';
    const projectWriteCounts = useConflictReviewStore(state => state.counts);
    const projectWriteReviewCount = projectWriteCounts.retryable + projectWriteCounts.conflictReview + projectWriteCounts.rejected;
    const isProjectRouteResolving = Boolean(routeProjectId && (!hasLoadedCloudProject || cloudProjectId !== routeProjectId));
    const hasResolvedToolPermissions = user?.isSuperAdmin === true || userRole !== null || canAccessProjectAsOrgAdmin || (!cloudEnabled && !isProjectRouteResolving);
    const toolNavigationSearch = useMemo(() => {
        const nextSearchParams = new URLSearchParams();

        for (const key of ['orgId', 'org', 'projectId']) {
            const value = searchParams.get(key);
            if (value) {
                nextSearchParams.set(key, value);
            }
        }

        const nextSearch = nextSearchParams.toString();
        return nextSearch ? `?${nextSearch}` : '';
    }, [searchParams]);

    useProjectWriteDispatcher({
        enabled: cloudEnabled,
        scope: projectWriteScope,
        userId: user?.uid ?? null,
    });

    useEffect(() => {
        if (!projectWriteScope) {
            setConflictReviewOpen(false);
        }
    }, [projectWriteScope]);

    useEffect(() => {
        if (hasLoadedCloudProject || !loadedRouteProjectRef.current) return;
        if (routeProjectLoadRef.current === loadedRouteProjectRef.current) {
            routeProjectLoadRef.current = null;
        }
        loadedRouteProjectRef.current = null;
    }, [hasLoadedCloudProject]);

    // Routing Guards: redirect super-admins to the right landing page
    useEffect(() => {
        if (!user) return;

        // Guard 1: First-time setup wizard
        if (user.needsSetup) {
            void navigate('/setup', { replace: true });
            return;
        }

        // Guard 2: Super-admin with no project context → admin command center.
        // Suppressed for the entire tab session once the user clicks "Back to App".
        // sessionStorage is per-tab and clears on tab close, so fresh visits
        // still auto-redirect. The flag is never consumed — it stays set so
        // subsequent tool navigations don't re-trigger the redirect.
        if (
            !sessionStorage.getItem('SKIP_ADMIN_REDIRECT') &&
            user.isSuperAdmin &&
            !searchParams.get('projectId') &&
            !cloudProjectId
        ) {
            void navigate('/admin', { replace: true });
            return;
        }
    }, [user, cloudProjectId, searchParams, navigate]);

    useEffect(() => {
        if (!user || !routeProjectId || hasInviteRouteParams) return;

        const routeKey = `${user.uid}:${routeOrgId || 'resolve'}:${routeProjectId}`;
        if (routeProjectLoadRef.current === routeKey) return;

        let isCancelled = false;
        routeProjectLoadRef.current = routeKey;

        const resolveOrgId = async (): Promise<string | null> => {
            if (routeOrgId) return routeOrgId;
            if (hasLoadedCloudProject && cloudOrgId && cloudProjectId === routeProjectId) return cloudOrgId;

            const projects = await getUserProjects(user.uid);
            const matches = projects.filter(project => project.id === routeProjectId);
            return (matches.find(project => project.orgId === cloudOrgId) || matches[0])?.orgId || null;
        };

        const openRouteProject = async () => {
            try {
                const orgId = await resolveOrgId();
                if (!orgId) {
                    throw new Error('Cannot open project without organization scope');
                }

                const projectScope = { orgId, projectId: routeProjectId };
                const projectData = await getCloudProject(projectScope);
                if (isCancelled) return;

                const { cloudState: nextCloudState, projectState } = splitCloudProjectSnapshotForHydration(projectData, {
                    orgId,
                    projectId: routeProjectId,
                    lastSync: new Date().toISOString(),
                });

                const { metaActions } = useAppStore.getState();
                metaActions.setProject(projectScope);
                useAppStore.setState(projectState);
                metaActions.setCloudState(nextCloudState);
                loadedRouteProjectRef.current = routeKey;
            } catch (error: unknown) {
                routeProjectLoadRef.current = null;
                loadedRouteProjectRef.current = null;
                const msg = error instanceof Error ? error.message : 'Unknown error';
            showToast(`Failed to open project from link: ${msg}`, 'error');
            }
        };

        void openRouteProject();

        return () => {
            isCancelled = true;
        };
    }, [user, routeProjectId, routeOrgId, hasInviteRouteParams, cloudOrgId, cloudProjectId, hasLoadedCloudProject]);

    const workspaceDefaultScopeKey = useMemo(() => {
        if (!cloudProjectId || !cloudMember?.userId || !cloudMember.roleId) {
            return null;
        }

        return `${cloudOrgId || 'local'}:${cloudProjectId}:${cloudMember.userId}:${cloudMember.roleId}`;
    }, [cloudOrgId, cloudProjectId, cloudMember?.userId, cloudMember?.roleId]);

    // Initialize workspace preference
    useEffect(() => {
        if (userPermissions.length === 0) {
            return;
        }

        const defaultWorkspace = getDefaultWorkspace(userPermissions);
        const isNewMemberScope = workspaceDefaultScopeKey !== null
            && workspaceDefaultScopeRef.current !== workspaceDefaultScopeKey;
        const isUnavailableWorkspace = activeWorkspace !== null
            && !accessibleWorkspaceIds.has(activeWorkspace);
        const shouldReplaceBackstage = isNewMemberScope
            && activeWorkspace === 'all-tools'
            && defaultWorkspace !== 'all-tools';

        if (activeWorkspace === null || isUnavailableWorkspace || shouldReplaceBackstage) {
            setActiveWorkspace(defaultWorkspace);
        }

        if (workspaceDefaultScopeKey !== null) {
            workspaceDefaultScopeRef.current = workspaceDefaultScopeKey;
        }
    }, [activeWorkspace, accessibleWorkspaceIds, userPermissions, setActiveWorkspace, workspaceDefaultScopeKey]);

    // Centralized Project Guard: Wipe all project data on project switch
    useEffect(() => {
        if (cloudProjectId) {
            useAppStore.getState().metaActions.setProject(cloudProjectId);
            logBetaActiveProjectDay(cloudProjectId);
        }
    }, [cloudProjectId]);

    // Sync URL route with activeTab
    // fallow-ignore-next-line complexity
    useEffect(() => {
        const currentPath = location.pathname;
        const matchingTool = getToolByRoute(currentPath);
        const currentActiveTool = getTool(activeTab);
        const activeTabNeedsResolvedPermissions = currentActiveTool?.requiredPermissions?.length || currentActiveTool?.requiresScriptText;
        const routeNeedsResolvedPermissions = matchingTool?.requiredPermissions?.length || matchingTool?.requiresScriptText;
        const routeNeedsPendingScriptAccess = Boolean(matchingTool?.requiresScriptText && !hasResolvedScriptTextAccess);
        const hasResolvedRouteAccess = hasResolvedToolPermissions && !routeNeedsPendingScriptAccess;

        if (!hasResolvedToolPermissions && activeTabNeedsResolvedPermissions && routeFallbackTool && routeFallbackTool.id !== activeTab) {
            setStoreActiveTab(routeFallbackTool.id);
            return;
        }

        if (matchingTool && !hasResolvedRouteAccess && routeNeedsResolvedPermissions) {
            return;
        }

        if (matchingTool && hasResolvedRouteAccess && !accessibleToolIds.has(matchingTool.id)) {
            if (routeFallbackTool && routeFallbackTool.id !== activeTab) {
                setStoreActiveTab(routeFallbackTool.id);
            }
            if (routeFallbackTool && currentPath !== routeFallbackTool.route) {
                void navigate({ pathname: routeFallbackTool.route, search: toolNavigationSearch }, { replace: true });
            }
            return;
        }

        if (hasResolvedRouteAccess && (!currentActiveTool || !accessibleToolIds.has(currentActiveTool.id))) {
            if (routeFallbackTool && routeFallbackTool.id !== activeTab) {
                setStoreActiveTab(routeFallbackTool.id);
            }
            if (routeFallbackTool && currentPath !== routeFallbackTool.route) {
                void navigate({ pathname: routeFallbackTool.route, search: toolNavigationSearch }, { replace: true });
            }
            return;
        }

        if (matchingTool && matchingTool.id !== activeTab) {
            setStoreActiveTab(matchingTool.id);
        }
        if (matchingTool && currentPath !== matchingTool.route) {
            void navigate({ pathname: matchingTool.route, search: location.search }, { replace: true });
        }
    }, [location.pathname, location.search, activeTab, setStoreActiveTab, navigate, accessibleToolIds, routeFallbackTool, hasResolvedToolPermissions, hasResolvedScriptTextAccess, toolNavigationSearch]);

    // Handle meta changes
    const handleMetaChange = (field: keyof typeof projectMeta, value: string) => {
        updateProjectMeta({ [field]: value });
    };

    // Logout
    const handleLogout = async () => {
        const confirmed = await confirmDialog('Are you sure you want to log out?');
        if (confirmed) {
            await logout();
            clearAuthUser();
            clearCloudSession();
            // Critical: clear localStorage LAST, synchronously before reload.
            // If cleared earlier, Zustand's persist middleware re-writes stale
            // data on any intermediate state change (e.g. the auth listener).
            localStorage.removeItem('onbook-storage');
            sessionStorage.removeItem('SKIP_ADMIN_REDIRECT');
            window.location.reload();
        }
    };

    const handleFeedbackClick = useCallback(() => {
        void openSentryFeedbackDialog()
            .then((opened) => {
                if (!opened) {
                    showToast('Feedback reporting is unavailable in this environment.', 'warning');
                }
            })
            .catch(() => {
                showToast('Feedback reporting could not be opened.', 'error');
            });
    }, []);

    // Undo/Redo
    const canUndo = pastStates.length > 0;
    const canRedo = futureStates.length > 0;
    const handleUndo = () => appUndo();
    const handleRedo = () => appRedo();

    // Get permission-filtered primary tools for current workspace
    // Uses same order-preserving logic as WorkspaceSidebar
    const workspacePrimaryToolIds = useMemo(() => {
        if (!activeWorkspace) {
            return [];
        }

        const workspace = getWorkspace(activeWorkspace);

        return workspace
            ? resolveWorkspacePrimaryToolIds(workspace, workspaceToolOrderOverrides)
            : [];
    }, [activeWorkspace, workspaceToolOrderOverrides]);
    
    const currentWorkspacePrimaryTools = useMemo(() => workspacePrimaryToolIds
        .map(id => TOOLS.find(t => t.id === id))
        .filter((tool): tool is ToolConfig => Boolean(tool && accessibleToolIds.has(tool.id))), [workspacePrimaryToolIds, accessibleToolIds]);

    // Build visibleTools to match exactly what WorkspaceSidebar displays:
    // - Dashboard is always first
    // - Then workspace primaryTools (filtered by permissions)
    // - Falls back to allAccessibleTools when no workspace selected
    const dashboardTool = TOOLS.find(t => t.id === 'dashboard');
    const usePrimaryTools = currentWorkspacePrimaryTools.length > 0;
    
    const visibleTools = useMemo(() => {
        const toolsAfterDashboard = usePrimaryTools
            ? currentWorkspacePrimaryTools.filter(t => t.id !== 'dashboard')
            : accessibleTools.filter(t => t.id !== 'dashboard');
        
        return [
            ...(dashboardTool ? [dashboardTool] : []),
            ...toolsAfterDashboard,
        ].slice(0, 9);
    }, [usePrimaryTools, currentWorkspacePrimaryTools, accessibleTools, dashboardTool]);

    // Command Palette
    const commandPalette = useCommandPalette({
        onSwitchWorkspace: (id) => {
            if (!accessibleWorkspaceIds.has(id as WorkspaceId)) return;
            setActiveWorkspace(id as WorkspaceId);
            const workspace = getWorkspace(id as WorkspaceId);
            if (workspace && workspace.primaryTools.length > 0) {
                handleToolSelect(workspace.primaryTools[0]!);
            }
        },
        onSwitchTool: (id) => {
            if (!accessibleToolIds.has(id as ToolId)) return;
            handleToolSelect(id as ToolId);
        },
        workspaces: accessibleWorkspaces,
        tools: accessibleTools,
    });

    // Keyboard navigation
    useKeyboardNavigation({
        onToolJump: (index) => {
            const tool = visibleTools[index];
            if (tool) {
                handleToolSelect(tool.id);
            }
        },
        onWorkspaceSelector: () => setWorkspaceSelectorOpen(true),
        onCommandPalette: () => commandPalette.toggle(),
        isEnabled: !isAuthModalOpen && !isAccountModalOpen && !isReportsModalOpen && !isWorkspaceSelectorOpen && !commandPalette.isOpen,
    });

    // Tool selection handler — wrapped in startTransition for non-blocking UI
    const handleToolSelect = (toolId: ToolId) => {
        startTransition(() => {
            setStoreActiveTab(toolId);
            void navigate({ pathname: getRouteForTool(toolId), search: toolNavigationSearch });
        });
    };

    // Lazy factory — only instantiates the component that's actually needed
    // fallow-ignore-next-line complexity
    const getToolComponent = (toolId: ToolId): React.ReactNode => {
        switch (toolId) {
            case 'dashboard': return <DashboardPage userPermissions={userPermissions} />;
            case 'show-structure': return <ShowStructureTab />;
            case 'rehearsal-script': return <RehearsalScriptTab />;
            case 'personnel': return <PersonnelTab searchTerm={searchTerm} />;
            case 'scheduler': return <SchedulerTab />;
            case 'calendar': return <CalendarView />;
            case 'notes': return <NotesTab />;
            case 'action-items': return <ActionItemsTab canAccessAsOrgAdmin={canAccessProjectAsOrgAdmin} />;
            case 'line-notes': return <LineNotesTab />;
            case 'set-builder': return <SetBuilderTab />;
            case 'blocking': return <BlockingTab />;
            case 'blocking-snapshot': return <div>Blocking Snapshot Placeholder</div>;
            case 'props': return <PropsTab searchTerm={searchTerm} />;
            case 'costumes': return <CostumesTab />;
            case 'sound': return <SoundTab />;
            case 'lighting': return <LightingTab />;
            case 'prompt-book': return <PromptBookTab />;
            case 'video': return <VideoView />;
            case 'email': return <EmailTab />;
            case 'feed': return <FeedTab />;
            case 'files': return <FileExplorer />;
            case 'scenes': return <RunSheetTab searchTerm={searchTerm} />;
            case 'budget': return <BudgetTab />;
            case 'production': return <ProductionTab />;
            case 'settings': return <SettingsTab />;
            case 'performance': return <PerformanceTab />;
            case 'activity-log': return <ActivityLogTab />;
            default:
                if (import.meta.env.DEV && toolId === ('dev' as ToolId)) return <KitchenSink />;
                return <ShowStructureTab />;
        }
    };

    const currentToolReports = TOOL_REPORTS[activeTab];
    const hasReports = !!currentToolReports && currentToolReports.length > 0;

    // Map tool IDs that support CSV import
    const toolHasCSVImport: Partial<Record<ToolId, boolean>> = {
        'production': true,
        'props': true,
    };
    const hasCSVImport = !!toolHasCSVImport[activeTab];

    const handleCSVImportFile = (files: FileList | null) => {
        const file = files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const content = evt.target?.result as string;
            if (content) {
                setCsvImportContent(content);
                setCsvImportSource(activeTab);
            }
        };
        reader.readAsText(file);
    };

    const renderFallbackToolId = routeFallbackTool?.id ?? 'show-structure';
    const renderableActiveTab = useMemo(() => getRenderableToolId({
        activeToolId: activeTab,
        cloudEnabled,
        fallbackToolId: renderFallbackToolId,
        scriptTextAccess: rawScriptTextAccess,
    }), [activeTab, cloudEnabled, rawScriptTextAccess, renderFallbackToolId]);
    const renderableSplitRightToolId = useMemo(() => getRenderableOptionalToolId({
        activeToolId: splitRightToolId,
        cloudEnabled,
        fallbackToolId: renderFallbackToolId,
        scriptTextAccess: rawScriptTextAccess,
    }), [cloudEnabled, rawScriptTextAccess, renderFallbackToolId, splitRightToolId]);
    const activeComponent = getToolComponent(renderableActiveTab);

    // Current workspace info
    const currentWorkspace = activeWorkspace ? getWorkspace(activeWorkspace) : undefined;

    // ── Device Gate: intercept tool rendering on small viewports ──
    const activeToolConfig = getTool(renderableActiveTab);
    const activeToolLabel = activeToolConfig?.label ?? 'Unknown';
    const deviceGate = useDeviceGate(activeToolConfig?.minWidth);

    const gatedComponent = deviceGate.isGated ? (
        <DeviceGateBanner
            toolName={activeToolConfig?.label || 'This tool'}
            minimumDevice={deviceGate.minimumLabel}
            onBypass={deviceGate.bypass}
        />
    ) : activeComponent;

    // Desktop-only guard: split view only on viewports >= 1024px
    const [isDesktop, setIsDesktop] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(min-width: 1024px)').matches : false
    );
    // Mobile detection: < 768px
    const [isMobile, setIsMobile] = useState(() =>
        typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
    );
    useEffect(() => {
        const desktopMq = window.matchMedia('(min-width: 1024px)');
        const mobileMq = window.matchMedia('(max-width: 767px)');
        const desktopHandler = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
        const mobileHandler = (e: MediaQueryListEvent) => {
            setIsMobile(e.matches);
            // Close mobile sidebar when resized above mobile breakpoint
            if (!e.matches) setMobileSidebarOpen(false);
        };
        desktopMq.addEventListener('change', desktopHandler, { passive: true });
        mobileMq.addEventListener('change', mobileHandler, { passive: true });
        return () => {
            desktopMq.removeEventListener('change', desktopHandler);
            mobileMq.removeEventListener('change', mobileHandler);
        };
    }, []);

    const isSidebarClickAwayPaused = trayOpen
        || isConflictReviewOpen
        || isAuthModalOpen
        || isAccountModalOpen
        || isReportsModalOpen
        || isWorkspaceSelectorOpen
        || commandPalette.isOpen;

    useEffect(() => {
        if (isMobile || sidebarCollapsed || isSidebarClickAwayPaused) {
            return;
        }

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target;

            if (!(target instanceof Node)) {
                return;
            }

            if (desktopSidebarRef.current?.contains(target)) {
                return;
            }

            toggleSidebarCollapsed();
        };

        document.addEventListener('pointerdown', handlePointerDown);

        return () => {
            document.removeEventListener('pointerdown', handlePointerDown);
        };
    }, [isMobile, isSidebarClickAwayPaused, sidebarCollapsed, toggleSidebarCollapsed]);

    const showSplitView = splitViewActive && isDesktop;

    // Companion persistence: remember last-used right-panel tool per active tool
    const COMPANION_PREFIX = 'onbook-split-companion:';

    const handleSplitActivate = useCallback(() => {
        // Always start with picker — let the user choose
        setSplitRightToolId(null);
        setSplitFocusedSide('left');
        setSplitViewActive(true);
    }, []);

    const handleSplitDeactivate = useCallback(() => {
        setSplitViewActive(false);
        setSplitRightToolId(null);
        setSplitFocusedSide('left');
    }, []);

    const handleRightToolSelect = useCallback((toolId: ToolId) => {
        setSplitRightToolId(toolId);
        // Persist this companion choice for next time
        try {
            localStorage.setItem(`${COMPANION_PREFIX}${activeTab}`, toolId);
        } catch { /* silently ignore */ }
    }, [activeTab]);

    const handlePanelFocus = useCallback((side: 'left' | 'right') => {
        setSplitFocusedSide(side);
        // Do NOT call setStoreActiveTab — the left panel always
        // uses the original activeTab. Mutating it causes blank pages.
    }, []);

    // Panel-aware ToolActionBar title
    const toolBarTitle = useMemo(() => {
        if (showSplitView) {
            const focusedToolId = splitFocusedSide === 'right' && splitRightToolId
                ? splitRightToolId
                : activeTab;
            const focusedTool = TOOLS.find(t => t.id === focusedToolId);
            const workspaceLabel = currentWorkspace?.label || '';
            return `${workspaceLabel} — ${focusedTool?.label || 'Unknown'}`;
        }
        return TOOLS.find(t => t.id === activeTab)?.label || 'Unknown';
    }, [showSplitView, splitFocusedSide, splitRightToolId, currentWorkspace, activeTab]);

    // Available tools for the right-panel picker (from accessible workspace tools)
    const splitPickerTools = useMemo(() => accessibleTools, [accessibleTools]);

    // Cloud status for Header
    const lastSync = useAppStore(state => state.cloud.lastSync);
    const syncStatus = getProjectWriteHeaderStatus({
        counts: projectWriteCounts,
        lastSync,
    });
    const { isOffline } = usePWA();
    const hidePhoneShellChrome = shouldHidePhoneShellChrome({
        isMobile,
        toolId: activeTab,
    });
    const mobileSidebarCloudLabel = cloudEnabled
        ? projectWriteReviewCount > 0
            ? 'Review needed'
            : isOffline
            ? 'Offline'
            : syncStatus === 'synced'
            ? 'Synced'
            : 'Syncing'
        : 'Local project';

    const toolActionLeftActions = (
        <>
            {/* Import Script button + Status Chip — Show Structure */}
            {activeTab === 'show-structure' && (
                <>
                    <ImportScriptButton />
                    <ScriptJobStatusChip jobState={scriptJobState} />
                </>
            )}

            {activeTab === 'props' && <ExtractPropsButton />}
            {activeTab === 'sound' && <ExtractSoundCuesButton />}
        </>
    );

    const toolActionSplitToggle = isDesktop ? (
        <Button
            aria-label={splitViewActive ? 'Single panel mode' : 'Split view mode'}
            onClick={() => splitViewActive ? handleSplitDeactivate() : handleSplitActivate()}
            className={`min-h-0 gap-1.5 rounded-md px-2.5 py-1.5 text-sm font-medium shadow-none transition-colors ${
                splitViewActive
                    ? 'bg-primary/15 text-primary dark:bg-primary/20'
                    : 'hover:bg-gray-100 dark:hover:bg-white/5 text-gray-500 dark:text-gray-400'
            }`}
            leftIcon={splitViewActive ? 'fullscreen' : 'vertical_split'}
            size="sm"
            title={splitViewActive ? 'Single panel mode' : 'Split view mode'}
            variant="ghost"
        >
            <span className="hidden sm:inline">
                {splitViewActive ? 'Single' : 'Split'}
            </span>
        </Button>
    ) : undefined;

    const toolActionRightActions = (
        <div className="flex items-center gap-2">
            {/* Search Input */}
            <div className="relative hidden sm:block">
                <Input
                    type="text"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.currentTarget.value)}
                    leftIcon="search"
                    fullWidth={false}
                    className="w-40 lg:w-56"
                />
            </div>
            <UndoRedoButtons
                onUndo={handleUndo}
                onRedo={handleRedo}
                canUndo={canUndo}
                canRedo={canRedo}
            />
            {hasCSVImport ? <FileUploadButton
                        accept=".csv"
                        onChange={handleCSVImportFile}
                        variant="ghost"
                        size="md"
                        className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
                        title="Import CSV"
                    >
                        <Icon name="upload" className="text-gray-600 dark:text-gray-400" />
                    </FileUploadButton> : null}
            {hasReports ? <Button
                    onClick={() => setReportsModalOpen(true)}
                    className="min-h-0 gap-2 rounded-md px-3 py-2 text-sm font-medium text-gray-700 shadow-none transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                    leftIcon="assignment"
                    size="sm"
                    title="Reports"
                    variant="ghost"
                >
                    Reports
                </Button> : null}
        </div>
    );

    const renderToolActionBar = ({
        className,
        testId,
        titleTestId,
    }: {
        className?: string;
        testId?: string;
        titleTestId?: string;
    } = {}) => (
        <ToolActionBar
            className={className}
            leftActions={toolActionLeftActions}
            rightActions={toolActionRightActions}
            splitToggle={toolActionSplitToggle}
            testId={testId}
            title={toolBarTitle}
            titleTestId={titleTestId}
        />
    );



    // Unread feed count is now calculated inside WorkspaceSidebar to avoid
    // re-rendering the entire layout tree on every feed update.


    return (
        <ProjectDataAuthorityProvider value={projectDataAuthority}>
            <div className="relative h-screen bg-background-light dark:bg-background-dark overflow-hidden" data-testid="main-layout">
            {/* Floating Sidebar — hidden on mobile, floating on tablet+ */}
            {!isMobile && (
                <motion.div
                    className={cn(
                        'absolute top-3 left-3 bottom-3 z-20',
                        sidebarCollapsed ? 'w-16' : 'w-64'
                    )}
                    data-testid="desktop-sidebar-overlay"
                    layout
                    ref={desktopSidebarRef}
                    transition={motionConfig.morph}
                >
                    <WorkspaceSidebar
                        activeWorkspace={activeWorkspace}
                        activeToolId={activeTab}
                        isCollapsed={sidebarCollapsed}
                        userPermissions={userPermissions}
                        onWorkspaceChange={(workspaceId) => {
                            setActiveWorkspace(workspaceId);
                            const workspace = getWorkspace(workspaceId);
                            if (workspace && workspace.primaryTools.length > 0) {
                                handleToolSelect(workspace.primaryTools[0]!);
                            }
                        }}
                        onToolSelect={handleToolSelect}
                        onToggleCollapse={toggleSidebarCollapsed}
                        onWorkspaceSelectorClick={() => setWorkspaceSelectorOpen(true)}
                        toolStatusMap={{
                            'show-structure': scriptJobState.indicator,
                        }}
                        unreadCount={unreadCount}
                        onNotificationsToggle={() => setTrayOpen((prev) => !prev)}
                    />
                </motion.div>
            )}

            {/* Mobile Sidebar Drawer — overlay on phones */}
            <AnimatePresence>
                {isMobile && isMobileSidebarOpen ? <div className="fixed inset-0 z-50 flex" data-testid="mobile-sidebar-overlay">
                        {/* Backdrop */}
                        <motion.div
                            className="absolute inset-0 bg-black/40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setMobileSidebarOpen(false)}
                        />
                        {/* Drawer */}
                        <motion.div
                            className="relative w-64 h-full z-10"
                            initial={{ x: -256 }}
                            animate={{ x: 0 }}
                            exit={{ x: -256 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                        >
                            <WorkspaceSidebar
                                activeWorkspace={activeWorkspace}
                                activeToolId={activeTab}
                                isCollapsed={false}
                                toggleIconSet="menu"
                                userPermissions={userPermissions}
                                onWorkspaceChange={(workspaceId) => {
                                    setActiveWorkspace(workspaceId);
                                    const workspace = getWorkspace(workspaceId);
                                    if (workspace && workspace.primaryTools.length > 0) {
                                        handleToolSelect(workspace.primaryTools[0]!);
                                    }
                                    setMobileSidebarOpen(false);
                                }}
                                onToolSelect={(toolId) => {
                                    handleToolSelect(toolId);
                                    setMobileSidebarOpen(false);
                                }}
                                onToggleCollapse={() => setMobileSidebarOpen(false)}
                                onWorkspaceSelectorClick={() => {
                                    setWorkspaceSelectorOpen(true);
                                    setMobileSidebarOpen(false);
                                }}
                                toolStatusMap={{
                                    'show-structure': scriptJobState.indicator,
                                }}
                                unreadCount={unreadCount}
                                onNotificationsToggle={() => {
                                    setTrayOpen((prev) => !prev);
                                    setMobileSidebarOpen(false);
                                }}
                                projectTitle={projectMeta.title}
                                user={user}
                                cloudLabel={mobileSidebarCloudLabel}
                                reviewCount={projectWriteReviewCount}
                                onAccountClick={() => {
                                    setAccountModalOpen(true);
                                    setMobileSidebarOpen(false);
                                }}
                                onLoginClick={() => {
                                    openAuthModal('signin');
                                    setMobileSidebarOpen(false);
                                }}
                                onReviewClick={() => {
                                    setConflictReviewOpen(true);
                                    setMobileSidebarOpen(false);
                                }}
                            />
                        </motion.div>
                    </div> : null}
            </AnimatePresence>

            {/* Main Content Area — full-width on mobile, offset by sidebar on tablet+ */}
            <div
                className={cn(
                    'h-full min-w-0 flex flex-col',
                    isMobile ? 'pl-0' : 'pl-22'
                )}
                data-testid="main-content-area"
            >
                {!hidePhoneShellChrome ? (
                    <div className="app-shell-chrome shrink-0 z-10" data-testid="app-shell-chrome">
                        <Header
                            user={user}
                            onLogin={() => openAuthModal('signin')}
                            onLogout={() => { void handleLogout(); }}
                            onProfileClick={() => setAccountModalOpen(true)}
                            showTitle={projectMeta.title}
                            currentLogo={projectMeta.leftLogo}
                            onLogoChange={(url) => handleMetaChange('leftLogo', url || '')}
                            rightLogo={headerRightLogo}
                            onRightLogoChange={() => { /* org logo is managed in Admin > Organization */ }}
                            cloudMode={cloudEnabled}
                            syncStatus={syncStatus}
                            isOffline={isOffline}
                            reviewCount={projectWriteReviewCount}
                            onReviewClick={() => setConflictReviewOpen((current) => !current)}
                            showFeedback={Boolean(cloudEnabled && isBetaProject)}
                            onFeedbackClick={handleFeedbackClick}
                            isMobile={isMobile}
                            onMenuClick={() => setMobileSidebarOpen(true)}
                        />

                        {/* Tool Action Bar - per-tool configuration */}
                        {renderToolActionBar()}
                    </div>
                ) : null}

                {hidePhoneShellChrome ? (
                    <>
                        <MobileShellLauncher onOpenNavigation={() => setMobileSidebarOpen(true)} />
                        {renderToolActionBar({
                            className: cn(
                                'fixed top-4 left-19 right-4 z-20 min-h-[44px] overflow-x-auto print:hidden',
                                'rounded-[12px]! px-3! py-2! pt-[env(safe-area-inset-top)]',
                                'scrollbar-none [&::-webkit-scrollbar]:hidden'
                            ),
                            testId: 'mobile-tool-header',
                            titleTestId: 'mobile-tool-title',
                        })}
                    </>
                ) : null}

                {showSplitView ? (
                    /* Split View — needs fixed height to fill remaining viewport */
                    <main
                        className={cn(
                            'flex-1 overflow-hidden min-h-0',
                            hidePhoneShellChrome && PHONE_SHELL_CONTENT_OFFSET_CLASS
                        )}
                        data-testid="main-split-view"
                    >
                        <ErrorBoundary tabName="Split View">
                            <SplitLayout
                                leftToolId={renderableActiveTab}
                                rightToolId={renderableSplitRightToolId}
                                getToolComponent={getToolComponent}
                                focusedSide={splitFocusedSide}
                                onPanelFocus={handlePanelFocus}
                                onRightToolSelect={handleRightToolSelect}
                                availableTools={splitPickerTools}
                                persistKey={currentWorkspace?.id}
                            />
                        </ErrorBoundary>
                    </main>
                ) : INTERNAL_SCROLL_TOOL_IDS.has(renderableActiveTab) ? (
                    <main
                        className={cn(
                            'flex-1 overflow-hidden min-h-0',
                            hidePhoneShellChrome && PHONE_SHELL_CONTENT_OFFSET_CLASS
                        )}
                        data-testid="main-tool-content"
                    >
                        <ErrorBoundary tabName={activeToolLabel}>
                            <React.Suspense fallback={
                                <div className="flex items-center justify-center h-64">
                                    <Spinner size="lg" />
                                </div>
                            }>
                                {gatedComponent}
                            </React.Suspense>
                        </ErrorBoundary>
                    </main>
                ) : (
                    <main
                        className={cn(
                            'flex-1 overflow-auto min-h-0',
                            hidePhoneShellChrome && PHONE_SHELL_CONTENT_OFFSET_CLASS
                        )}
                        data-testid="main-tool-content-scrollable"
                        onScroll={handleMainScroll}
                        ref={mainScrollRef}
                    >
                        <div className="w-full max-w-[1600px] mx-auto p-4 lg:p-6 print:p-0">
                            <ErrorBoundary tabName={activeToolLabel}>
                                <React.Suspense fallback={
                                    <div className="flex items-center justify-center h-64">
                                        <Spinner size="lg" />
                                    </div>
                                }>
                                    {gatedComponent}
                                </React.Suspense>
                            </ErrorBoundary>
                        </div>
                    </main>
                )}
            </div>

            {/* Modals */}
            {isAuthModalOpen ? <React.Suspense fallback={null}>
                    <AuthModal
                        isOpen={isAuthModalOpen}
                        onClose={() => setAuthModalOpen(false)}
                        initialMode={authModalInitialMode}
                    />
                </React.Suspense> : null}
            {isAccountModalOpen ? <React.Suspense fallback={null}>
                    <AccountModal
                        isOpen={isAccountModalOpen}
                        onClose={() => setAccountModalOpen(false)}
                        onLogout={() => { void handleLogout(); }}
                        user={user}
                    />
                </React.Suspense> : null}
            {isReportsModalOpen ? <React.Suspense fallback={null}>
                    <ReportsModal
                        isOpen={isReportsModalOpen}
                        onClose={() => {
                            setReportsModalOpen(false);
                            setActiveReportId(null);
                        }}
                        filterReports={currentToolReports}
                        initialReportId={currentToolReports?.[0]}
                        onActiveReportChange={setActiveReportId}
                        userPermissions={userPermissions}
                    />
                </React.Suspense> : null}
            <WorkspaceSelector
                isOpen={isWorkspaceSelectorOpen}
                currentWorkspace={activeWorkspace}
                userPermissions={userPermissions}
                onSelect={(workspaceId) => {
                    setActiveWorkspace(workspaceId);
                    const workspace = getWorkspace(workspaceId);
                    if (workspace && workspace.primaryTools.length > 0) {
                        handleToolSelect(workspace.primaryTools[0]!);
                    }
                }}
                onClose={() => setWorkspaceSelectorOpen(false)}
            />
            {csvImportContent && csvImportSource === 'production' ? <React.Suspense fallback={null}>
                    <ImportDeadlinesModal
                        csvContent={csvImportContent}
                        onClose={() => { setCsvImportContent(null); setCsvImportSource(null); }}
                    />
                </React.Suspense> : null}
            {csvImportContent && csvImportSource === 'props' ? <React.Suspense fallback={null}>
                    <ImportPropsModal
                        csvContent={csvImportContent}
                        onClose={() => { setCsvImportContent(null); setCsvImportSource(null); }}
                    />
                </React.Suspense> : null}

            {/* Barry AI Agent — Floating Action Button + Chat Panel */}
            {showBarryPanel ? (
                <React.Suspense fallback={null}>
                    <BarryPanel />
                </React.Suspense>
            ) : null}

            {/* Command Palette — Ctrl+K */}
            <React.Suspense fallback={null}>
                <CommandPaletteComponent palette={commandPalette} />
            </React.Suspense>

            {/* Notification Tray */}
            <NotificationTray
                isOpen={trayOpen}
                onClose={() => setTrayOpen(false)}
                notifications={notifications}
                loading={notificationsLoading}
                onNavigate={(path) => { void navigate(path); }}
                sidebarCollapsed={sidebarCollapsed}
                isMobile={isMobile}
            />
            <ConflictReviewPanel
                isOpen={isConflictReviewOpen}
                onClose={() => setConflictReviewOpen(false)}
                scope={projectWriteScope}
            />
            </div>
        </ProjectDataAuthorityProvider>
    );
};
